import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card } from 'react-bootstrap';
import { userService } from '../../services/apiService';
import { useAuthToken } from '../../hooks/useAuthToken';
import { LoadingState, ErrorState, serverMessage } from '../PageState';
import StackedTable from '../StackedTable';
import SegmentedControl from '../SegmentedControl';
import { EMPTY, formatDate, formatDateTime, plural } from '../../utils/format';

const FIRST_ROWS = 20;

const CHANNELS = [
  { value: 'ALL', label: 'All' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'TEAMS', label: 'Teams' },
];

const TITLES = {
  TEAMS_WHEREABOUTS_REMINDER: 'Whereabouts reminder',
  TEAMS_TEST_NOTIFICATION: 'Test notification',
  EMAIL_CHECK_IN: 'Email check-in',
  WHEREABOUTS_REMINDER_EMAIL: 'Whereabouts reminder (latest)',
  PASSWORD_RESET_EMAIL: 'Password reset',
  EMAIL_BLOCKED: 'Address blocked',
};

const details = (event) => {
  switch (event.type) {
    case 'EMAIL_CHECK_IN':
      return (
        <>
          <Badge bg={event.confirmed ? 'success' : 'secondary'} className="me-2">
            {event.confirmed ? 'Confirmed' : 'Not confirmed'}
          </Badge>
          {event.detail || null}
        </>
      );
    case 'WHEREABOUTS_REMINDER_EMAIL':
      return <span className="text-muted">Earlier ones aren&apos;t kept. Also set when a colleague sends a reminder.</span>;
    case 'EMAIL_BLOCKED': {
      const what = event.reason === 'COMPLAINT' ? 'Marked an email as spam' : 'Bounced';
      const times = event.eventCount > 1 ? ` ${plural(event.eventCount, 'time')} since ${formatDate(event.firstSeenAt)}` : '';
      return (
        <>
          <Badge bg={event.clearedAt ? 'secondary' : 'warning'} className="me-2">
            {event.clearedAt ? `Unblocked ${formatDate(event.clearedAt)}` : 'Still blocked'}
          </Badge>
          {what}{times}
          {event.detail && <span className="d-block small text-muted">{event.detail}</span>}
        </>
      );
    }
    default:
      return EMPTY;
  }
};

/**
 * The notifications the database has a record of for this user. It's a partial history: leave,
 * invite and most other emails, and Teams leave messages, aren't recorded anywhere yet.
 */
const UserNotificationHistory = ({ user }) => {
  const refreshToken = useAuthToken();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [channel, setChannel] = useState('ALL');
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await refreshToken();
      const response = await userService.getUserNotificationHistory(user.id);
      setData(response.data);
    } catch (err) {
      setError(err);
      console.error('Failed to load past notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [refreshToken, user.id]);

  // A changed address can have a different block history
  useEffect(() => {
    load();
  }, [load, user.email]);

  let body;
  if (loading && !data) {
    body = <LoadingState label="Loading past notifications" />;
  } else if (error) {
    body = <ErrorState message={serverMessage(error, 'Could not load past notifications.')} onRetry={load} inCard />;
  } else {
    const events = data.events.filter((event) => channel === 'ALL' || event.channel === channel);
    const shown = showAll ? events : events.slice(0, FIRST_ROWS);
    body = (
      <>
        {events.length === 0 ? (
          <p className="text-muted p-4 mb-0">
            {data.events.length === 0 ? 'No recorded notifications.' : 'None recorded for this channel.'}
          </p>
        ) : (
          <StackedTable responsive hover className="mb-0 align-middle">
            <thead>
              <tr>
                <th>When</th>
                <th>Channel</th>
                <th>Notification</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((event, index) => (
                <tr key={`${event.type}-${event.at}-${index}`}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {event.dateOnly ? formatDate(event.at) : formatDateTime(event.at)}
                  </td>
                  <td>{event.channel === 'TEAMS' ? 'Teams' : 'Email'}</td>
                  <td>{TITLES[event.type] || event.type}</td>
                  <td>{details(event)}</td>
                </tr>
              ))}
            </tbody>
          </StackedTable>
        )}
        {events.length > FIRST_ROWS && (
          <div className="px-3 py-2 border-top">
            <Button variant="link" size="sm" className="p-0" onClick={() => setShowAll((value) => !value)}>
              {showAll ? `Show the latest ${FIRST_ROWS}` : `Show all ${events.length}`}
            </Button>
          </div>
        )}
        <Card.Footer className="small text-muted">
          Only some notifications are recorded: Teams whereabouts reminders, Teams test messages they sent
          themselves, email check-in emails, password reset emails, the latest whereabouts reminder email and
          blocked addresses. Leave, invite and other emails, and Teams leave messages, aren&apos;t recorded. A
          Teams reminder is recorded even when their Teams notifications were off or the message failed. A
          test message is only recorded when Teams accepted it.
          {data.truncated && ` Only the latest ${data.limit} of each kind are shown.`}
          {!data.suppressionChecked && ' Blocked addresses couldn\'t be checked.'}
        </Card.Footer>
      </>
    );
  }

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="bg-light d-flex flex-wrap align-items-center justify-content-between gap-2">
        <h5 className="mb-0">Past notifications</h5>
        {data && !error && data.events.length > 0 && (
          <SegmentedControl
            options={CHANNELS}
            value={channel}
            onChange={(value) => { setChannel(value); setShowAll(false); }}
            ariaLabel="Channel"
          />
        )}
      </Card.Header>
      {body}
    </Card>
  );
};

export default UserNotificationHistory;
